import React from 'react'
import { useNavigate } from 'react-router-dom'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import type { Value as PhoneValue } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { LocationInput } from '../../../components/ui'
import { Skeleton } from '../../../components/ui/Skeleton'
import { PageShell } from './PageShell'
import { SettingsTabButtonIcon } from '../components/SettingsTabButtonIcon'
import { clearAuth, loadAuth } from '../../../lib/authStorage'
import { useDashboard } from '../DashboardContext'
import { uploadToCloudinary } from '../../../lib/cloudinaryUpload'
import { USER_AVATAR } from '../../../lib/endpoints'
import { ImageCropModal } from '../../../components/ui/ImageCropModal'
import { apiGet, apiPost, apiPut, apiDelete } from '../../../lib/api'
import {
  BUSINESS,
  USER_CHANGE_PASSWORD,
  USER_SET_PASSWORD,
  USER_DEVICE,
  USER_DEVICES,
  USER_DEVICES_REVOKE_ALL,
  USER_ME,
  USER_PROFILE,
} from '../../../lib/endpoints'
import type { BusinessInfo, DevicesResponse, UserProfile } from '../../../types'

type PersonalFieldId = 'fullname' | 'email' | 'phone' | 'location'

type SessionIconKind = 'desktop' | 'phone'

type SessionRow = {
  id: string
  name: string
  meta: string
  current: boolean
  revoked: boolean
  icon: SessionIconKind
}

type ProfileTabId = 'tab-personal' | 'tab-preferences' | 'tab-security' | 'tab-sessions'

const PROFILE_NAV_ITEMS: { id: ProfileTabId; variant: 'personal' | 'preferences' | 'security' | 'sessions'; label: string }[] = [
  { id: 'tab-personal',    variant: 'personal',    label: 'Personal'    },
  { id: 'tab-preferences', variant: 'preferences', label: 'Preferences' },
  { id: 'tab-security',    variant: 'security',    label: 'Security'    },
  { id: 'tab-sessions',    variant: 'sessions',    label: 'Sessions'    },
]

const readLabelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace",
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
}
const readValueStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--text)', lineHeight: 1.5,
}
const readEmptyStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--text3)', fontStyle: 'italic',
}
const labelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6, display: 'block',
}
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '9px 12px', fontSize: 14, color: 'var(--text)',
  outline: 'none', boxSizing: 'border-box',
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div style={readLabelStyle}>{label}</div>
      {value ? <div style={readValueStyle}>{value}</div> : <div style={readEmptyStyle}>—</div>}
    </div>
  )
}

function formatMemberSince(value?: string | null): string {
  if (!value) return 'Member since -'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return 'Member since -'
  return `Member since ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`
}

function formatRelativeLastLogin(value?: string | null): string {
  if (!value) return 'Last login -'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return 'Last login -'
  const diffMs = Date.now() - d.getTime()
  if (diffMs < 60_000) return 'Last login just now'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `Last login ${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `Last login ${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `Last login ${days}d ago`
  return `Last login ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
}

function checkPasswordRules(pw: string) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[!@#$%^&*()_+\[\]{}|;:,.<>?/`~]/.test(pw),
  }
}

export function ProfilePage({ isActive }: { isActive: boolean }) {
  const navigate = useNavigate()
  const isAdmin = loadAuth()?.role === 'ADMIN'
  const { profilePendingPercent } = useDashboard()
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null)
  const [avatarUploading, setAvatarUploading] = React.useState(false)
  const [cropSrc, setCropSrc] = React.useState<string | null>(null)
  const [activeTab, setActiveTab] = React.useState<ProfileTabId>('tab-personal')

  const [profileLoading, setProfileLoading] = React.useState(true)
  const [phoneValue, setPhoneValue] = React.useState<PhoneValue | undefined>(undefined)
  const [phoneError, setPhoneError] = React.useState<string | null>(null)

  const [drafts, setDrafts] = React.useState<Record<PersonalFieldId, string>>({
    fullname: '', email: '', phone: '', location: '',
  })

  const [serverValues, setServerValues] = React.useState<Record<PersonalFieldId, string>>({
    fullname: '', email: '', phone: '', location: '',
  })

  const [personalEditing, setPersonalEditing] = React.useState(false)
  const [personalSaving, setPersonalSaving] = React.useState(false)
  const [personalError, setPersonalError] = React.useState<string | null>(null)

  const [prefEmail] = React.useState(true)
  const [prefWeekly] = React.useState(false)
  const [prefUpdates] = React.useState(true)

  const [newPw, setNewPw] = React.useState('')
  const [currentPassword, setCurrentPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [authProvider, setAuthProvider] = React.useState<string>('LOCAL')
  const [showCurrentPw, setShowCurrentPw] = React.useState(false)
  const [showNewPw, setShowNewPw] = React.useState(false)
  const [showConfirmPw, setShowConfirmPw] = React.useState(false)
  const pwRules = checkPasswordRules(newPw)

  const [sessions, setSessions] = React.useState<SessionRow[]>([])
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [businessName, setBusinessName] = React.useState<string>('Business')
  const [memberSinceLabel, setMemberSinceLabel] = React.useState<string>('Member since -')
  const [lastLoginLabel, setLastLoginLabel] = React.useState<string>('Last login -')

  React.useEffect(() => {
    return () => {
      if (avatarUrl && avatarUrl.startsWith('blob:')) URL.revokeObjectURL(avatarUrl)
    }
  }, [avatarUrl])

  React.useEffect(() => {
    if (!isActive) return

    let cancelled = false

    async function load() {
      try {
        setActionError(null)

        const [me, devices] = await Promise.all([
          apiGet<UserProfile>(USER_ME),
          apiGet<DevicesResponse>(USER_DEVICES),
        ])

        if (cancelled) return

        const fullName = `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim()
        const phone = me.phone ?? ''
        setPhoneValue((phone as PhoneValue) || undefined)
        const location = me.location ?? ''
        const serverAvatarUrl = (me.avatarUrl as string | null | undefined) ?? null

        const next: Record<PersonalFieldId, string> = {
          fullname: fullName,
          email: me.email ?? '',
          phone,
          location,
        }

        setServerValues(next)
        setDrafts(next)
        setAvatarUrl(serverAvatarUrl)
        setAuthProvider(me.authProvider ?? 'LOCAL')
        setMemberSinceLabel(formatMemberSince(me.createdAt))

        const mappedSessions: SessionRow[] = (devices.devices ?? []).map((d, idx) => {
          const icon: SessionIconKind = d.deviceType === 'MOBILE' ? 'phone' : 'desktop'
          const isCurrent = idx === 0
          const ip = d.ipAddress ? String(d.ipAddress) : '—'
          const lastUsed = d.lastUsed ? new Date(d.lastUsed) : null
          const lastUsedText =
            !lastUsed
              ? ''
              : isCurrent
                ? 'Active now'
                : `${Math.max(1, Math.round((Date.now() - lastUsed.getTime()) / (1000 * 60 * 60)))} hours ago`
          return {
            id: d.id,
            name: d.deviceName ?? 'Unknown device',
            meta: `${ip}${lastUsedText ? ` · ${lastUsedText}` : ''}`,
            current: isCurrent,
            revoked: false,
            icon,
          }
        })

        setSessions(mappedSessions)
        const currentDeviceLastUsed = devices.devices?.[0]?.lastUsed ?? null
        setLastLoginLabel(formatRelativeLastLogin(currentDeviceLastUsed))

        if (!isAdmin) {
          try {
            const business = await apiGet<BusinessInfo>(BUSINESS)
            if (!cancelled) setBusinessName((business.businessName ?? '').trim() || 'Business')
          } catch {
            if (!cancelled) setBusinessName('Business')
          }
        }
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) {
          clearAuth()
          navigate('/signin', { replace: true })
          return
        }
        if (!cancelled) setActionError(err.message ?? 'Failed to load profile')
      } finally {
        if (!cancelled) setProfileLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [isActive, navigate])

  function cancelPersonalEdit() {
    setDrafts(serverValues)
    setPhoneValue((serverValues.phone as PhoneValue) || undefined)
    setPhoneError(null)
    setPersonalError(null)
    setPersonalEditing(false)
  }

  function savePersonalInfo() {
    async function persist() {
      if (phoneError) return
      setPersonalSaving(true)
      setPersonalError(null)

      const parts = drafts.fullname.trim().split(/\s+/).filter(Boolean)
      const firstName = parts[0] ?? ''
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : firstName

      const payload = {
        firstName,
        lastName,
        phone: drafts.phone,
        location: drafts.location,
      }

      try {
        const updated = await apiPut<UserProfile, typeof payload>(USER_PROFILE, payload)

        const fullName = `${updated.firstName ?? ''} ${updated.lastName ?? ''}`.trim()
        const normalizedPhone = updated.phone ?? drafts.phone ?? ''
        const next: Record<PersonalFieldId, string> = {
          fullname: fullName,
          email: updated.email ?? drafts.email,
          phone: normalizedPhone,
          location: updated.location ?? '',
        }

        setServerValues(next)
        setDrafts(next)
        setPhoneValue((normalizedPhone as PhoneValue) || undefined)
        setPersonalEditing(false)
        setPersonalError(null)
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) {
          clearAuth()
          navigate('/signin', { replace: true })
          return
        }
        setPersonalError(err.message ?? 'Failed to save profile')
      } finally {
        setPersonalSaving(false)
      }
    }

    void persist()
  }

  function updatePassword() {
    async function run() {
      setIsSubmitting(true)
      setActionError(null)

      if (confirmPassword !== newPw) {
        setActionError('New password and confirm password do not match.')
        setIsSubmitting(false)
        return
      }

      try {
        await apiPost<unknown, Record<string, unknown>>(USER_CHANGE_PASSWORD, {
          currentPassword,
          newPassword: newPw,
        })

        clearAuth()
        navigate('/signin', { replace: true })
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) {
          clearAuth()
          navigate('/signin', { replace: true })
          return
        }
        setActionError(err.message ?? 'Failed to update password')
      } finally {
        setIsSubmitting(false)
      }
    }

    void run()
  }

  function setPassword() {
    async function run() {
      setIsSubmitting(true)
      setActionError(null)

      if (confirmPassword !== newPw) {
        setActionError('Passwords do not match.')
        setIsSubmitting(false)
        return
      }

      try {
        await apiPost<unknown, Record<string, unknown>>(USER_SET_PASSWORD, { newPassword: newPw })
        clearAuth()
        navigate('/signin', { replace: true })
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) {
          clearAuth()
          navigate('/signin', { replace: true })
          return
        }
        setActionError(err.message ?? 'Failed to set password')
      } finally {
        setIsSubmitting(false)
      }
    }

    void run()
  }

  function revokeAll() {
    async function run() {
      setIsSubmitting(true)
      setActionError(null)
      try {
        await apiPost<unknown, Record<string, never>>(USER_DEVICES_REVOKE_ALL, {})
        const devicesRes = await apiGet<DevicesResponse>(USER_DEVICES)

        setSessions((devicesRes.devices ?? []).map((d, idx) => {
          const icon: SessionIconKind = d.deviceType === 'MOBILE' ? 'phone' : 'desktop'
          const isCurrent = idx === 0
          const ip = d.ipAddress ? String(d.ipAddress) : '—'
          const lastUsed = d.lastUsed ? new Date(d.lastUsed) : null
          const lastUsedText =
            !lastUsed ? '' : isCurrent ? 'Active now'
              : `${Math.max(1, Math.round((Date.now() - lastUsed.getTime()) / (1000 * 60 * 60)))} hours ago`
          return {
            id: d.id, name: d.deviceName ?? 'Unknown device',
            meta: `${ip}${lastUsedText ? ` · ${lastUsedText}` : ''}`,
            current: isCurrent, revoked: false, icon,
          }
        }))
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) { clearAuth(); navigate('/signin', { replace: true }); return }
        setActionError(err.message ?? 'Failed to revoke all sessions')
      } finally {
        setIsSubmitting(false)
      }
    }
    void run()
  }

  function revokeSession(id: string) {
    async function run() {
      setIsSubmitting(true)
      setActionError(null)
      try {
        await apiDelete(USER_DEVICE(id))
        const devicesRes2 = await apiGet<DevicesResponse>(USER_DEVICES)
        setSessions((devicesRes2.devices ?? []).map((d, idx) => {
          const icon: SessionIconKind = d.deviceType === 'MOBILE' ? 'phone' : 'desktop'
          const isCurrent = idx === 0
          const ip = d.ipAddress ? String(d.ipAddress) : '—'
          const lastUsed = d.lastUsed ? new Date(d.lastUsed) : null
          const lastUsedText =
            !lastUsed ? '' : isCurrent ? 'Active now'
              : `${Math.max(1, Math.round((Date.now() - lastUsed.getTime()) / (1000 * 60 * 60)))} hours ago`
          return {
            id: d.id, name: d.deviceName ?? 'Unknown device',
            meta: `${ip}${lastUsedText ? ` · ${lastUsedText}` : ''}`,
            current: isCurrent, revoked: false, icon,
          }
        }))
      } catch (e) {
        const err = e as Error & { status?: number }
        if (err.status === 401 || err.status === 403) { clearAuth(); navigate('/signin', { replace: true }); return }
        setActionError(err.message ?? 'Failed to revoke session')
      } finally {
        setIsSubmitting(false)
      }
    }
    void run()
  }

  function SessionIcon({ kind }: { kind: 'desktop' | 'phone' }) {
    if (kind === 'phone') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      )
    }
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    )
  }

  function handleAvatarCropConfirm(blob: Blob) {
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })
    if (cropSrc) { URL.revokeObjectURL(cropSrc); setCropSrc(null) }
    setAvatarUploading(true)
    uploadToCloudinary(file, 'profile-avatars')
      .then(({ secureUrl }) =>
        apiPut<{ avatarUrl: string }, { avatarUrl: string }>(USER_AVATAR, { avatarUrl: secureUrl })
          .then(() => setAvatarUrl(secureUrl))
      )
      .catch(err => setActionError((err as Error).message ?? 'Avatar upload failed'))
      .finally(() => setAvatarUploading(false))
  }

  return (
    <PageShell id="page-profile" isActive={isActive}>
      {cropSrc && (
        <ImageCropModal
          srcUrl={cropSrc}
          variant="avatar"
          onConfirm={handleAvatarCropConfirm}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null) }}
        />
      )}
      <div className="content" style={{ position: 'relative' }}>
        {actionError ? (
          <div style={{ marginBottom: 16, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--red)', padding: '10px 14px', borderRadius: 10, fontSize: 13 }}>
            {actionError}
          </div>
        ) : null}

        {/* ── Compact hero ── */}
        <div className="profile-hero">
          <div className="profile-avatar-wrap">
            {profileLoading ? (
              <div className="profile-avatar skeleton" aria-hidden="true" style={{ color: 'transparent' }} />
            ) : (
              <div
                className="profile-avatar"
                onClick={() => !avatarUploading && document.getElementById('avatarInput')?.click()}
                style={{
                  ...(avatarUrl
                    ? { backgroundImage: `url(${avatarUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }
                    : undefined),
                  cursor: avatarUploading ? 'wait' : 'pointer',
                  opacity: avatarUploading ? 0.6 : 1,
                }}
              >
                {avatarUrl ? null : (serverValues.fullname.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?') as string}
                {avatarUploading ? (
                  <div className="avatar-edit-badge" title="Uploading…">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" />
                    </svg>
                  </div>
                ) : (
                  <div className="avatar-edit-badge" title="Change photo">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </div>
                )}
              </div>
            )}
            <input
              type="file"
              id="avatarInput"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0]
                if (!f) return
                e.target.value = ''
                setCropSrc(URL.createObjectURL(f))
              }}
            />
          </div>

          <div className="profile-hero-body">
            {profileLoading ? (
              <>
                <div className="profile-hero-title">
                  <Skeleton height={18} width={160} />
                  <Skeleton height={12} width={200} />
                </div>
                <div className="profile-hero-info" aria-hidden="true">
                  <Skeleton height={18} width={72} borderRadius={99} />
                  <Skeleton height={18} width={92} borderRadius={99} />
                  <Skeleton height={18} width={110} borderRadius={99} />
                  <div className="profile-meta-sep" />
                  <Skeleton height={12} width={140} />
                  <Skeleton height={12} width={80} />
                  <Skeleton height={12} width={80} />
                  <Skeleton height={12} width={110} />
                </div>
              </>
            ) : (
              <>
                <div className="profile-hero-title">
                  <span className="profile-name">{serverValues.fullname || '—'}</span>
                  <span className="profile-role">
                    {isAdmin ? 'System Administrator' : `Account Owner · ${businessName}`}
                  </span>
                </div>
                <div className="profile-hero-info">
                  {isAdmin
                    ? <span className="profile-badge pb-verified">⬡ Admin</span>
                    : <span className="profile-badge pb-plan">✦ Free Plan</span>
                  }
                  <span className="profile-badge pb-verified">✓ Email Verified</span>
                  <span className="profile-badge pb-member">{memberSinceLabel}</span>
                  <div className="profile-meta-sep" />
                  <div className="profile-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    {serverValues.email || '—'}
                  </div>
                  <div className="profile-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6 19.79 19.79 0 0 1 1.61 5 2 2 0 0 1 3.6 2.82h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.4a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 18z" />
                    </svg>
                    {serverValues.phone || '—'}
                  </div>
                  <div className="profile-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="10" r="3" />
                      <path d="M12 2a8 8 0 0 0-8 8c0 5.33 8 12 8 12s8-6.67 8-12a8 8 0 0 0-8-8z" />
                    </svg>
                    {serverValues.location || '—'}
                  </div>
                  <div className="profile-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {lastLoginLabel}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Profile completion banner ── */}
        {!isAdmin && !profileLoading && typeof profilePendingPercent === 'number' && profilePendingPercent < 100 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 20,
          }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--accent-bg)',
              border: '1px solid var(--accent-bd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                Complete your profile — {profilePendingPercent}% done
              </div>
              <div style={{ height: 5, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${profilePendingPercent}%`,
                  background: 'var(--accent)',
                  borderRadius: 99,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            </div>
            <button
              className="btn-primary"
              style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
              onClick={() => setActiveTab('tab-personal')}
            >
              Fill in details
            </button>
          </div>
        )}

        {/* Tab bar */}
        <div className="settings-tabbar" role="tablist" aria-label="Profile sections">
          {PROFILE_NAV_ITEMS.map(({ id, variant, label }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              className={`settings-tabbar-item${activeTab === id ? ' active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <SettingsTabButtonIcon variant={variant} />
              {label}
            </button>
          ))}
        </div>

        <div className="content">

          {/* ── Personal Information ── */}
          {activeTab === 'tab-personal' && (
            <div className="settings-panel" role="tabpanel">
              <div className="settings-section">
                <div
                  className="settings-section-title"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Personal Information</span>
                  {!personalEditing && (
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 12, padding: '3px 12px' }}
                      onClick={() => setPersonalEditing(true)}
                    >
                      Edit
                    </button>
                  )}
                  {personalEditing && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '3px 12px' }}
                        onClick={cancelPersonalEdit}
                        disabled={personalSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-primary"
                        style={{ fontSize: 12, padding: '3px 14px' }}
                        onClick={savePersonalInfo}
                        disabled={personalSaving || !!phoneError}
                      >
                        {personalSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {personalError && (
                  <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
                    {personalError}
                  </div>
                )}

                {profileLoading ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                    {Array.from({ length: 4 }, (_, i) => (
                      <div key={i}>
                        <Skeleton height={10} width={70} style={{ marginBottom: 6 }} />
                        <Skeleton height={14} width={150} />
                      </div>
                    ))}
                  </div>
                ) : !personalEditing ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                    <ReadField label="Full Name" value={serverValues.fullname} />
                    <ReadField label="Email" value={serverValues.email} />
                    <ReadField label="Phone" value={serverValues.phone} />
                    <ReadField label="Location" value={serverValues.location} />
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                    <div>
                      <label style={labelStyle}>Full Name</label>
                      <input
                        style={inputStyle}
                        value={drafts.fullname}
                        onChange={e => setDrafts(v => ({ ...v, fullname: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Email</label>
                      <input
                        style={{ ...inputStyle, color: 'var(--text3)', cursor: 'not-allowed' }}
                        value={drafts.email}
                        readOnly
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Phone</label>
                      <PhoneInput
                        international
                        defaultCountry="US"
                        value={phoneValue}
                        onChange={v => {
                          setPhoneValue(v)
                          setDrafts(prev => ({ ...prev, phone: v ?? '' }))
                          setPhoneError(v && !isValidPhoneNumber(v) ? 'Enter a valid phone number.' : null)
                        }}
                        className="ier-phone"
                      />
                      {phoneError && (
                        <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{phoneError}</div>
                      )}
                    </div>
                    <div>
                      <label style={labelStyle}>Location</label>
                      <LocationInput
                        style={inputStyle}
                        value={drafts.location}
                        onChange={v => setDrafts(prev => ({ ...prev, location: v }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Preferences ── */}
          {activeTab === 'tab-preferences' && (
            <div className="settings-panel" role="tabpanel">
              <div className="settings-section">
                <div className="settings-section-title">Preferences</div>
                <div className="toggle-row" style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', opacity: 0.5 }}>
                  <div>
                    <div className="toggle-label">Email notifications <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>· Coming soon</span></div>
                    <div className="toggle-sub">Receive updates by email</div>
                  </div>
                  <div className={`toggle-switch ${prefEmail ? 'on' : ''}`} style={{ pointerEvents: 'none' }} />
                </div>
                <div className="toggle-row" style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', opacity: 0.5 }}>
                  <div>
                    <div className="toggle-label">Weekly digest <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>· Coming soon</span></div>
                    <div className="toggle-sub">Summary of activity every Monday</div>
                  </div>
                  <div className={`toggle-switch ${prefWeekly ? 'on' : ''}`} style={{ pointerEvents: 'none' }} />
                </div>
                <div className="toggle-row" style={{ padding: '14px 0', opacity: 0.5 }}>
                  <div>
                    <div className="toggle-label">Product updates <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>· Coming soon</span></div>
                    <div className="toggle-sub">News about Nudge features</div>
                  </div>
                  <div className={`toggle-switch ${prefUpdates ? 'on' : ''}`} style={{ pointerEvents: 'none' }} />
                </div>
              </div>
            </div>
          )}

          {/* ── Security ── */}
          {activeTab === 'tab-security' && (
            <div className="settings-panel" role="tabpanel">
              <div className="settings-section">
                <div className="settings-section-title">Password &amp; Security</div>

                {/* ── OAuth banner (Google users only) ── */}
                {authProvider === 'GOOGLE' && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', marginBottom: 20, background: 'var(--bg-card, #F5EFE6)', border: '1.5px solid var(--border)', borderRadius: 10 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }} fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/></svg>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>You signed in with Google</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>Set a Nudge password below to also sign in with your email and password. Your Google login will keep working.</div>
                    </div>
                  </div>
                )}

                {/* ── Current Password (local accounts only) ── */}
                {authProvider !== 'GOOGLE' && (
                <div className="form-row">
                  <label className="form-label">Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showCurrentPw ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, lineHeight: 1 }}
                      aria-label={showCurrentPw ? 'Hide password' : 'Show password'}
                    >
                      {showCurrentPw ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                )}

                <div className="form-row">
                  <label className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showNewPw ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPw}
                      onChange={e => setNewPw(e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, lineHeight: 1 }}
                      aria-label={showNewPw ? 'Hide password' : 'Show password'}
                    >
                      {showNewPw ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  {newPw.length > 0 && (
                    <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                      {([
                        [pwRules.length,  '8+ characters'],
                        [pwRules.upper,   'Uppercase letter'],
                        [pwRules.lower,   'Lowercase letter'],
                        [pwRules.digit,   'Number'],
                        [pwRules.special, 'Special character'],
                      ] as [boolean, string][]).map(([pass, label]) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: pass ? 'var(--green, #16a34a)' : 'var(--text3)' }}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            {pass
                              ? <><circle cx="6" cy="6" r="6" fill="#16a34a"/><path d="M3.5 6l1.8 1.8 3.2-3.6" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></>
                              : <circle cx="6" cy="6" r="5.5" stroke="currentColor" strokeWidth="1"/>
                            }
                          </svg>
                          {label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-row" style={{ marginBottom: 20 }}>
                  <label className="form-label">Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      type={showConfirmPw ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPw(v => !v)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, lineHeight: 1 }}
                      aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPw ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4, borderTop: '1px solid var(--border)', opacity: 0.5 }}>
                  <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'DM Mono',monospace" }}>
                    Two-factor auth: <span style={{ color: 'var(--orange)' }}>Not enabled</span>
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'inherit', fontWeight: 400 }}>· Coming soon</span>
                  </div>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 11, padding: '5px 12px', pointerEvents: 'none' }}
                    type="button"
                    disabled
                  >
                    Enable 2FA
                  </button>
                </div>
                <button
                  className="btn-primary"
                  style={{ marginTop: 18, width: '100%', justifyContent: 'center' }}
                  onClick={authProvider === 'GOOGLE' ? setPassword : updatePassword}
                  disabled={isSubmitting}
                >
                  {authProvider === 'GOOGLE' ? 'Set Password' : 'Update Password'}
                </button>
              </div>
            </div>
          )}

          {/* ── Sessions ── */}
          {activeTab === 'tab-sessions' && (
            <div className="settings-panel" role="tabpanel">
              <div className="settings-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Active Sessions</span>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 11, padding: '5px 12px', color: 'var(--red)', borderColor: 'var(--red-bd)' }}
                    onClick={revokeAll}
                    disabled={isSubmitting}
                  >
                    Revoke All
                  </button>
                </div>
                {sessions.map(s => (
                  <div className="session-row" key={s.id} style={s.revoked ? { opacity: 0.4 } : undefined}>
                    <div className="session-icon">
                      <SessionIcon kind={s.icon} />
                    </div>
                    <div className="session-info">
                      <div className="session-name">{s.name}</div>
                      <div className="session-meta">{s.meta}</div>
                    </div>
                    {s.current ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="session-current">Current</span>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: 11, padding: '4px 10px', color: 'var(--red)', borderColor: 'var(--red-bd)' }}
                          onClick={() => { clearAuth(); navigate('/signin', { replace: true }) }}
                        >
                          Sign out
                        </button>
                      </div>
                    ) : (
                      <button className="btn-revoke" onClick={() => revokeSession(s.id)}>
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </PageShell>
  )
}
